import React, { useEffect, useRef, useState } from "react";
import { Typography, Input, Button, message, Upload, Modal, Avatar } from "antd";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_API_CONFIG } from "../../util/Api";
import { Popover, Menu } from "antd";
import { DownOutlined, LeftOutlined, SendOutlined, PaperClipOutlined, FilePdfOutlined, FileImageOutlined, CloseOutlined, CameraOutlined } from "@ant-design/icons";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";

const { Dragger } = Upload;

const ChatDetails = ({
  selectedChat,
  sendMessage,
  setSendMessage,
  handleSendMessage,
  onMessageSent,
  onChatUpdate,
  onBack,
  onClose,
}) => {
  const messagesEndRef = useRef(null);
  const authUser = useSelector(({ auth }) => auth.userData);
  const userId = authUser.id;
  const token = authUser.token;
  const { isMobile } = useGetBreakpoints();
  const [sending, setSending] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [animationState, setAnimationState] = useState('entering'); // 'entering', 'visible', 'exiting'
  const [previousChatId, setPreviousChatId] = useState(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChat?.messages]);

  const formatIndianTime = (msgTimestamp) => {
    return new Date(msgTimestamp).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

   // Enhanced animation effect for chat switching
  useEffect(() => {
    const currentChatId =
      selectedChat?.quotationId ||
      selectedChat?.enquiryId ||
      selectedChat?.promotionId ||
      selectedChat?.id;

    if (!currentChatId) {
      return undefined;
    }

    if (previousChatId === null) {
      setPreviousChatId(currentChatId);
      setAnimationState("entering");

      const enterTimer = setTimeout(() => {
        setAnimationState("visible");
      }, 300);

      return () => clearTimeout(enterTimer);
    }

    if (currentChatId === previousChatId) {
      setAnimationState("visible");
      return undefined;
    }

    setAnimationState("exiting");

    const exitTimer = setTimeout(() => {
      setAnimationState("entering");
      setPreviousChatId(currentChatId);
    }, 150);

    const enterTimer = setTimeout(() => {
      setAnimationState("visible");
    }, 450);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(enterTimer);
    };
  }, [
    selectedChat?.quotationId,
    selectedChat?.enquiryId,
    selectedChat?.promotionId,
    selectedChat?.id,
    previousChatId,
  ]);

  const handleClose = () => {
    if (isMobile) {
      onBack();
    } else {
      setAnimationState('exiting');
      setTimeout(() => {
        onClose?.();
      }, 300);
    }
  };

  const isValidString = (str) => {
  return str && typeof str === 'string' && str.trim().length > 0;
};

  const logoFromString = (str) => {
  if (!isValidString(str)) return "";
  const words = str.trim().split(" ");
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  } else {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
};

  const sortedMessages = selectedChat?.messages
    ? [...selectedChat.messages].sort(
        (a, b) =>
          new Date(a.timestamp || a.createdAt) -
          new Date(b.timestamp || b.createdAt)
      )
    : [];

  const chatPartnerId =
    selectedChat?.chatPartnerId ||
    (selectedChat?.senderId === userId
      ? selectedChat?.receiverId
      : selectedChat?.senderId);

  const chatPartner =
    selectedChat?.senderId === userId
      ? selectedChat?.receiver
      : selectedChat?.sender;

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';
    const isLt10M = file.size / 1024 / 1024 < 10;
    
    if (!isImage && !isPDF) {
      message.error('You can only upload image or PDF files!');
      return false;
    }
    
    if (!isLt10M) {
      message.error('File must smaller than 10MB!');
      return false;
    }
    
    return true;
  };

  const handlePreview = async (file) => {
    if (file.type.startsWith('image/')) {
      setPreviewImage(file.url || file.thumbUrl || file.preview);
      setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
      setPreviewVisible(true);
    } else if (file.type === 'application/pdf') {
      window.open(file.url || file.thumbUrl || file.preview, '_blank');
    }
  };

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handleRemove = (file) => {
    setFileList(fileList.filter(item => item.uid !== file.uid));
  };

  const handleSendChatMessage = async () => {
    if ((!sendMessage.trim() && fileList.length === 0) || sending) {
      message.warning("Please enter a message or attach a file!");
      return;
    }

    if (!chatPartnerId) {
      message.warning("Unable to identify the chat recipient.");
      return;
    }

    if (userId === chatPartnerId) {
      message.warning("You cannot send a message to yourself.");
      return;
    }

    setSending(true);

    try {
      // First upload files if any
      let attachments = [];
      if (fileList.length > 0) {
        const formData = new FormData();
        fileList.forEach(file => {
          formData.append('files', file.originFileObj);
        });

      //   const uploadResponse = await axios.post(
      //     `${BASE_API_CONFIG.baseURL}/messages/upload`,
      //     formData,
      //     {
      //       headers: {
      //         'Authorization': `Bearer ${token}`,
      //         'Content-Type': 'multipart/form-data',
      //       }
      //     }
      //   );

      //   attachments = uploadResponse.data.files.map(file => ({
      //     url: file.url,
      //     type: file.type,
      //     name: file.name
      //   }));
      }

      // Then send the message with attachments
      const response = await axios.post(
        `${BASE_API_CONFIG.baseURL}/messages`,
        {
          text: sendMessage,
          senderId: userId,
          sender: authUser.name,
          receiverId: chatPartnerId,
          receiver: chatPartner?.companyName || chatPartner?.name || "User",
          enquiryId: selectedChat.enquiryId,
          quotationId: selectedChat.quotationId,
          // attachments : attachments,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // "Content-Type": "multipart/form-data", // Required for file uploads

          },
        }
      );

      if (response.status === 200) {
        const newMessage = {
          text: sendMessage,
          senderId: userId,
          timestamp: new Date().toISOString(),
          attachments: response.data.attachments
        };

        const updatedChat = {
          ...selectedChat,
          messages: [...selectedChat.messages, newMessage],
        };

        onChatUpdate(updatedChat);
        setSendMessage("");
        setFileList([]);
        onMessageSent();
      } else {
        message.error("Message sent, but with a non-standard status.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (msg) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this message?"
    );
    if (confirmDelete) {
      try {
        const response = await axios.delete(
          `${BASE_API_CONFIG.baseURL}/messages/delete-message/${msg.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.status === 200) {
          message.success("Message deleted successfully!");
          onMessageSent();
        }
      } catch (error) {
        console.error("Error deleting message:", error);
        message.error("Failed to delete message.");
      }
    }
  };

  const renderMessageContent = (msg) => {
    return (
      <>
        {msg.text && (
          <div style={{ marginBottom: msg.attachments?.length > 0 ? 8 : 0 }}>
            {msg.text}
          </div>
        )}
        
        {msg.attachments?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {msg.attachments.map((file, index) => (
              <div 
                key={index}
                style={{
                  border: '1px solid rgba(24, 144, 255, 0.2)',
                  borderRadius: 8,
                  padding: 8,
                  background: 'rgba(24, 144, 255, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  ':hover': {
                    background: 'rgba(24, 144, 255, 0.1)'
                  }
                }}
                onClick={() => window.open(file.url, '_blank')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {file.type.startsWith('image/') ? (
                    <FileImageOutlined style={{ fontSize: 20, color: '#0062FF' }} />
                  ) : (
                    <FilePdfOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
                  )}
                  <Typography.Text
                    ellipsis
                    style={{
                      flex: 1,
                      color: msg.senderId === userId ? 'white' : '#333'
                    }}
                  >
                    {file.name}
                  </Typography.Text>
                </div>
                
                {file.type.startsWith('image/') && (
                  <img
                    src={file.url}
                    alt={file.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      marginTop: 8,
                      borderRadius: 4
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const getAnimationClass = () => {
    switch (animationState) {
      case 'entering':
        return 'chat-entering';
      case 'visible':
        return 'chat-visible';
      case 'exiting':
        return 'chat-exiting';
      default:
        return '';
    }
  };

  return (
    <div
      className={`chat-container ${getAnimationClass()}`}
      style={{
        flex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f8fafc",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(24, 144, 255, 0.08)",
        border: "1px solid rgba(24, 144, 255, 0.1)",
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
            70% { box-shadow: 0 0 0 8px rgba(82, 196, 26, 0); }
            100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
          }
          
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes chatEnter {
            from {
              opacity: 0;
              transform: translateX(50px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
          }

          @keyframes chatExit {
            from {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
            to {
              opacity: 0;
              transform: translateX(-50px) scale(0.9);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Chat container transitions */
          .chat-container {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .chat-entering {
            animation: chatEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }

          .chat-visible {
            opacity: 1;
            transform: translateX(0) scale(1);
          }

          .chat-exiting {
            animation: chatExit 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          /* Message animations */
          .message-container {
            animation-duration: 0.4s;
            animation-fill-mode: both;
            animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          .message-left {
            animation-name: slideInLeft;
          }
          
          .message-right {
            animation-name: slideInRight;
          }

          /* Enhanced message animations for entering chat */
          .chat-entering .message-container {
            animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            animation-delay: calc(0.1s + (var(--message-index, 0) * 0.05s));
            opacity: 0;
          }

          .chat-visible .message-container {
            opacity: 1;
          }
          
          .message-container:hover .down-arrow {
            display: block !important;
          }
          
          .message-bubble {
            position: relative;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          .message-bubble:hover {
            transform: translateY(-2px);
          }
          
          .typing-indicator {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 12px 16px;
            background: white;
            border-radius: 20px 20px 20px 6px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(24, 144, 255, 0.1);
            max-width: 80px;
          }
          
          .typing-dot {
            width: 8px;
            height: 8px;
            background: #1890ff;
            border-radius: 50%;
            animation: typing 1.4s infinite ease-in-out;
          }
          
          .typing-dot:nth-child(1) { animation-delay: -0.32s; }
          .typing-dot:nth-child(2) { animation-delay: -0.16s; }
          
          @keyframes typing {
            0%, 80%, 100% {
              transform: scale(0);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          .send-btn-disabled {
            background: linear-gradient(135deg, #1890ff 0%, #1890ff 100%) !important;
            border: none !important;
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4) !important;
            cursor: not-allowed !important;
          }
          
          .send-btn-enabled {
            background: linear-gradient(135deg, #1890ff 0%, #1890ff 100%) !important;
            border: none !important;
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4) !important;
          }
          
          .send-btn-enabled:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 6px 16px rgba(24, 144, 255, 0.5) !important;
          }
        `}
      </style>

      {/* Chat Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1890ff 0%, #0062FF 100%)",
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          borderRadius: "20px 20px 0 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background elements */}
         <div style={{ display: "flex", alignItems: "center", gap: "16px", zIndex: 1 }}>
          {isMobile && onBack && (
            <Button
              type="text"
              onClick={onBack}
              style={{
                color: "white",
                border: "none",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
              }}
            >
              <LeftOutlined style={{ fontSize: "18px" }} />
            </Button>
          )}

         {/* Profile Picture */}
      <div style={{ zIndex: 1 }}>
    <Avatar
      src={chatPartner?.companyLogo}
      size={48}
      style={{
        border: "3px solid rgba(255, 255, 255, 0.3)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      {selectedChat && logoFromString(chatPartner?.companyName || chatPartner?.name)}
    </Avatar>
  </div>

        <div style={{ flex: 1, zIndex: 1 }}>
          <Typography.Title
            level={4}
            style={{
              color: "white",
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
            >
            {selectedChat
              ? `${chatPartner?.companyName || chatPartner?.name || "User"} (${
                  selectedChat.businessType
                })`
              : "No chats selected"}
          </Typography.Title>

         
          <Typography.Text
            style={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "12px",
              marginTop: "4px",
              display:"flex",
              gap:"4px",
            }}
          >
             <div
          style={{
            width: "12px",
            height: "12px",
            backgroundColor: "#52c41a",
            borderRadius: "50%",
            border: "2px solid white",
            zIndex: 1,
            animation: "pulse 2s infinite",
          }}
        />

            {sending ? "Typing..." : "Online now"}
          </Typography.Text>
        </div>
     </div>



      {!isMobile && (
          <Button
            type="text"
            onClick={handleClose}
            style={{
              color: "white",
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              transition: "all 0.3s ease",
              ':hover': {
                background: "rgba(255, 255, 255, 0.3)",
              }
            }}
          >
            <CloseOutlined style={{ fontSize: "16px" }} />
          </Button>
        )}
        {/* Online indicator */}
        {/* <div
          style={{
            width: "12px",
            height: "12px",
            backgroundColor: "#52c41a",
            borderRadius: "50%",
            border: "2px solid white",
            zIndex: 1,
            animation: "pulse 2s infinite",
          }}
        /> */}
      </div>

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
          position: "relative",
        }}
      >
        {sortedMessages.map((msg, index) => (
          <div
            key={index}
            className={`message-container ${
              msg.senderId === userId ? "message-right" : "message-left"
            }`}
            style={{
              alignSelf: msg.senderId === userId ? "flex-end" : "flex-start",
              maxWidth: "75%",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              className="message-bubble"
              style={{
                background:
                  msg.senderId === userId
                    ? "linear-gradient(135deg, #1890ff 0%, #1890ff 100%)"
                    : "white",
                color: msg.senderId === userId ? "white" : "#333",
                padding: "12px 16px",
                borderRadius:
                  msg.senderId === userId
                    ? "20px 20px 6px 20px"
                    : "20px 20px 20px 6px",
                wordBreak: "break-word",
                position: "relative",
                // boxShadow:
                //   msg.senderId === userId
                //     ? "0 4px 12px rgba(24, 144, 255, 0.3)"
                //     : "0 4px 12px rgba(0, 0, 0, 0.1)",
                border:
                  msg.senderId === userId
                    ? "none"
                    : "1px solid rgba(24, 144, 255, 0.1)",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              {renderMessageContent(msg)}
              
              {/* Delete option */}
              <div
                style={{
                  position: "absolute",
                  top: "-8px",
                  [msg.senderId === userId ? "right" : "left"]: "-8px",
                  display: "none",
                }}
                className="down-arrow"
              >
                <Popover
                  content={
                    <Menu style={{ border: "none", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                      <Menu.Item
                        key="delete"
                        onClick={() => handleDeleteMessage(msg)}
                        style={{
                          color: "#ff4d4f",
                          borderRadius: "6px",
                          fontWeight: "500",
                        }}
                      >
                        Delete Message
                      </Menu.Item>
                    </Menu>
                  }
                  trigger="click"
                  placement={
                    msg.senderId === userId ? "bottomRight" : "bottomLeft"
                  }
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      backdropFilter: "blur(5px)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "rgba(0, 0, 0, 0.2)";
                      e.target.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
                      e.target.style.transform = "scale(1)";
                    }}
                  >
                    <DownOutlined
                      style={{
                        fontSize: "10px",
                        color: "white",
                      }}
                    />
                  </div>
                </Popover>
              </div>
            </div>

            <Typography.Text
              style={{
                fontSize: "11px",
                color: "#999",
                alignSelf: msg.senderId === userId ? "flex-end" : "flex-start",
                marginTop: "-4px",
                paddingLeft: msg.senderId === userId ? "0" : "8px",
                paddingRight: msg.senderId === userId ? "8px" : "0",
              }}
            >
              {formatIndianTime(msg.timestamp || msg.createdAt)}
            </Typography.Text>
          </div>
        ))}

        {/* Typing indicator when sending */}
        {sending && (
          <div
            style={{
              alignSelf: "flex-start",
              maxWidth: "75%",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        style={{
          backgroundColor: "white",
          borderTop: "1px solid rgba(24, 144, 255, 0.1)",
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          borderRadius: "0 0 20px 20px",
        }}
      >
        {/* File preview area */}
        {fileList.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 12,
              padding: 8,
              background: 'rgba(24, 144, 255, 0.05)',
              borderRadius: 8
            }}
          >
            {fileList.map(file => (
              <div
                key={file.uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 8px',
                  background: 'white',
                  borderRadius: 4,
                  border: '1px solid rgba(24, 144, 255, 0.2)'
                }}
              >
                {file.type?.startsWith('image/') ? (
                  <FileImageOutlined style={{ color: '#0062FF' }} />
                ) : (
                  <FilePdfOutlined style={{ color: '#ff4d4f' }} />
                )}
                <Typography.Text
                  ellipsis
                  style={{ maxWidth: 100 }}
                >
                  {file.name}
                </Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined style={{ fontSize: 12 }} />}
                  onClick={() => handleRemove(file)}
                />
              </div>
            ))}
          </div>
        )}

     <div style={{ 
  display: 'flex', 
  alignItems: 'center', // Align all items vertically center
  gap: 8, 
  padding: '8px 0' // Add some vertical padding
}}>
  {/* Camera and Attachment buttons - now properly aligned */}
  <div style={{ 
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    
  }}>
    {/* <Upload
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      onPreview={handlePreview}
      multiple
      showUploadList={false}
      accept="image/*"
    >
      <Button
        type="text"
        icon={<CameraOutlined style={{ fontSize: 20 }} />}
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          top:"8px",
        }}
      />
    </Upload>
    
    <Upload
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      onPreview={handlePreview}
      multiple
      showUploadList={false}
      accept=".pdf,image/*"
    >
      <Button
        type="text"
        icon={<PaperClipOutlined style={{ fontSize: 20 }} />}
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          marginLeft: 4,
          top:"8px",
        }}
      />
    </Upload> */}
  </div>

  {/* Text input area - properly aligned with buttons */}
  <div style={{ 
    flex: 1,
    display: 'flex',
    alignItems: 'center'
  }}>
    <Input.TextArea
      placeholder="Type your message..."
      value={sendMessage}
      onChange={(e) => setSendMessage(e.target.value)}
      onPressEnter={(e) => {
        if (!e.shiftKey) {
          e.preventDefault();
          handleSendChatMessage();
        }
      }}
      disabled={sending}
      autoSize={{ minRows: 1, maxRows: 4 }}
      style={{
        borderRadius: "20px",
        border: "2px solid rgba(24, 144, 255, 0.2)",
        padding: "12px 16px",
        fontSize: "14px",
        resize: "none",
        transition: "all 0.3s ease",
        backgroundColor: "#fafbfc",
        width: '100%',
        margin: 0
      }}
    />
  </div>

  {/* Send button - properly aligned */}
  <Button
    type="primary"
    onClick={handleSendChatMessage}
    loading={sending}
    className={
      (sendMessage.trim() || fileList.length > 0) && !sending
        ? "send-btn-enabled"
        : "send-btn-disabled"
    }
    style={{
      borderRadius: "50%",
      width: "45px",
      height: "45px",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      cursor: (sendMessage.trim() || fileList.length > 0) && !sending ? "pointer" : "not-allowed",
      flexShrink: 0,
      marginLeft: 8,
      top:"8px",
    }}
  >
    <SendOutlined style={{ fontSize: "16px" }} />
  </Button>
</div>
      </div>

      {/* Image Preview Modal */}
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default ChatDetails;
